import { FC, useContext, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { UpsertAdminRoofCategoryBody } from "../../../../../http/patrol/types/roofCategories";
import { useRoofCategoriesApi } from "../../../../../http/patrol/useRoofCategories";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import Switch from "../../../../ui/Switch";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface InsertRoofCategoryForm {
  name: string | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  isFlat: boolean;
  minPitch: number | null;
  maxPitch: number | null;
  defaultPitch: number | null;
}

const NewRoofCategory: FC = () => {
  const { systemId } = useParams<{ systemId: string }>();
  const navigate = useNavigate();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const roofCategoriesApi = useRoofCategoriesApi();

  const onSubmit = () => async (formData: InsertRoofCategoryForm) => {
    const body: UpsertAdminRoofCategoryBody = {
      name: formData.name!,
      nameDe: null,
      nameEs: null,
      nameFr: null,
      nameIt: null,
      order: formData.order!,
      visibility: formData.visibility,
      isFlat: formData.isFlat,
      minPitch: formData.minPitch,
      maxPitch: formData.maxPitch,
      defaultPitch: formData.defaultPitch,
    };

    setIsSubmitting(true);

    try {
      const res = await roofCategoriesApi.insertAdminRoofCategory(body);
      navigate(`/admin/systems/${systemId}/roof_categories/${res.id}`);
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form<InsertRoofCategoryForm>
      defaultValues={{
        name: null,
        order: 50,
        visibility: "hidden",
      }}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, control, setValue, watch }) => {
        const flat = !!watch("isFlat");

        return (
          <FormFieldsContainer fullWidth>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isInvalid, isOptional }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isSubmitting}
                  {...register("name", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
            <Controller
              name="minPitch"
              control={control}
              rules={{ max: 100 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.min} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isSubmitting}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      suffix=" °"
                      onBlur={(value) => setValue("minPitch", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="maxPitch"
              control={control}
              rules={{ max: 100 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.maxPitch} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isSubmitting}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      suffix=" °"
                      onBlur={(value) => setValue("maxPitch", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="defaultPitch"
              control={control}
              rules={{ max: 100 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.defaultPitch} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isSubmitting}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      suffix=" °"
                      onBlur={(value) => setValue("defaultPitch", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <FormField label={i18n.translation.common.isFlat} isOptional>
              {({ labelId }) => (
                <Switch id={labelId} checked={flat} onChange={(checked) => setValue("isFlat", checked)} />
              )}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              disabled={isSubmitting}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default NewRoofCategory;
